import { useCallback, useEffect, useState } from 'react'

/** Run an async loader, keeping the last good value while a new one is in flight. */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(loader, deps)

  useEffect(() => {
    let live = true
    setLoading(true)
    run()
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e) => { if (live) setError(e) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [run])

  return { data, error, loading }
}

/** Delay rapidly-changing input (a text filter) before it reaches the API. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}
